import React, { useEffect, useMemo, useState } from 'react';
import { useSuggestedObjetsForBox } from '../hooks/useBoxes';
import { useMoveBulk } from '../hooks/useObjets';
import { useToastContext } from '../contexts/ToastContext';

interface BulkAddToBoxModalProps {
  isOpen: boolean;
  onClose: () => void;
  box: {
    id: string;
    nom: string;
    code: string;
    theme?: string | null;
  } | null;
}

const BulkAddToBoxModal: React.FC<BulkAddToBoxModalProps> = ({ isOpen, onClose, box }) => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const boxId = box?.id ?? '';
  const { data, isLoading, error, refetch } = useSuggestedObjetsForBox(boxId, isOpen && !!boxId);
  const moveBulkMutation = useMoveBulk();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
      setSearch('');
    }
  }, [isOpen, boxId]);

  const suggestions = data?.objets ?? [];

  const filteredSuggestions = useMemo(() => {
    if (!search.trim()) return suggestions;
    const q = search.toLowerCase();
    return suggestions.filter(
      (objet) =>
        objet.nom?.toLowerCase().includes(q) ||
        objet.categorie?.toLowerCase().includes(q) ||
        objet.sousCategorie?.toLowerCase().includes(q),
    );
  }, [suggestions, search]);

  if (!isOpen || !box) return null;

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    setSelectedIds(filteredSuggestions.map((o) => o.id));
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      showWarning('Sélection requise', 'Sélectionnez au moins un objet.');
      return;
    }

    try {
      await moveBulkMutation.mutateAsync({
        objetIds: selectedIds,
        boxId: box.id,
      });
      showSuccess('Objets ajoutés', `${selectedIds.length} objet(s) ajouté(s) à la box « ${box.nom} ».`);
      await refetch();
      onClose();
    } catch (err: any) {
      showError('Erreur', err.response?.data?.message || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  📦 Remplir la box « {box.nom} »
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Thème : {box.theme || '—'} — objets hors box correspondants
                </p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            {!box.theme?.trim() && (
              <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                Définissez un thème sur cette box (ex. « déguisement », « Décors & costumes ») pour voir des propositions.
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer les suggestions…"
                className="flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handleSelectAll}
                className="px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Tout sélectionner ({filteredSuggestions.length})
              </button>
            </div>

            {isLoading && <p className="text-sm text-gray-500">Chargement des suggestions…</p>}
            {error && (
              <p className="text-sm text-red-600">Impossible de charger les suggestions.</p>
            )}

            {!isLoading && !error && filteredSuggestions.length === 0 && (
              <p className="text-sm text-gray-500 py-4 text-center">
                {data?.message || 'Aucun objet sans box ne correspond à ce thème.'}
              </p>
            )}

            <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-md">
              {filteredSuggestions.map((objet) => (
                <li key={objet.id} className="flex items-start gap-3 p-3 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(objet.id)}
                    onChange={() => toggleSelection(objet.id)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{objet.nom}</p>
                    <p className="text-xs text-gray-500">
                      {[objet.categorie, objet.sousCategorie].filter(Boolean).join(' › ')}
                    </p>
                    {objet.zone && (
                      <p className="text-xs text-gray-400 mt-0.5">Zone : {objet.zone.nom}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{objet.etat}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={moveBulkMutation.isPending || selectedIds.length === 0}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {moveBulkMutation.isPending
                ? 'Ajout…'
                : `Ajouter ${selectedIds.length} objet(s) à la box`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAddToBoxModal;
